import React from "react";
import { Check, Minus } from "lucide-react";

const FEATURES: { label: string; ws: boolean; piq: boolean; bundle: boolean }[] = [
  // WealthScope core
  { label: "Net worth dashboard & tracking",         ws: true,  piq: false, bundle: true },
  { label: "Buying power & investment capacity",     ws: true,  piq: false, bundle: true },
  { label: "Debt-to-income (DTI) analysis",          ws: true,  piq: false, bundle: true },
  { label: "Monthly cash flow breakdown",            ws: true,  piq: false, bundle: true },
  { label: "Bank account sync (Plaid)",              ws: true,  piq: false, bundle: true },
  { label: "Deal readiness scoring",                 ws: true,  piq: false, bundle: true },
  { label: "AI-powered financial insights",          ws: true,  piq: false, bundle: true },
  { label: "Automated alerts & notifications",       ws: true,  piq: true,  bundle: true },
  { label: "Financial reports (PDF)",                ws: true,  piq: false, bundle: true },
  // PortfolioIQ core
  { label: "Unlimited property tracking",            ws: false, piq: true,  bundle: true },
  { label: "Cap rate & cash-on-cash returns",        ws: false, piq: true,  bundle: true },
  { label: "DSCR analysis & lender PDF reports",     ws: false, piq: true,  bundle: true },
  { label: "Schedule E tax reports (for your CPA)",  ws: false, piq: true,  bundle: true },
  { label: "Portfolio-level analytics",              ws: false, piq: true,  bundle: true },
  { label: "What-if refinance analysis",             ws: false, piq: true,  bundle: true },
  { label: "AI underperformance alerts",             ws: false, piq: true,  bundle: true },
  { label: "Market value benchmarking",              ws: false, piq: true,  bundle: true },
  // Bundle only
  { label: "Priority support",                       ws: false, piq: false, bundle: true },
  { label: "Early access to new features",           ws: false, piq: false, bundle: true },
  { label: "Unlimited bank connections",             ws: false, piq: false, bundle: true },
];

const Y = () => <Check className="w-4 h-4 text-blue-400 mx-auto" />;
const N = () => <Minus className="w-4 h-4 dark:text-white/15 text-gray-300 mx-auto" />;

export const FeaturesGrid: React.FC = () => {
  return (
    <section className="px-4 sm:px-6 pb-14 sm:pb-16 mx-auto max-w-[900px]">
      <h2 className="text-2xl sm:text-3xl font-extrabold dark:text-white text-gray-900 text-center mb-2">
        Compare plans
      </h2>
      <p className="dark:text-slate-500 text-gray-500 text-center text-sm mb-6 sm:mb-10">
        All plans include a 14-day free trial. We'll email you before your trial ends — cancel anytime.
      </p>

      {/* Mobile layout (no horizontal scroll) */}
      <div className="md:hidden dark:bg-white/[0.02] bg-white border dark:border-white/[0.08] border-blue-300 rounded-2xl overflow-hidden shadow-sm">
        <div className="grid grid-cols-4 dark:border-b dark:border-white/8 border-b border-gray-200 bg-gray-50/70 dark:bg-white/[0.02]">
          <div className="p-2 text-[10px] font-semibold tracking-wide text-gray-500 dark:text-slate-400">Feature</div>
          <div className="p-2 text-center">
            <p className="text-[10px] font-bold text-blue-500 leading-tight">WealthScope</p>
            <p className="text-[10px] font-semibold text-gray-700 dark:text-slate-300 leading-tight">$9.99/mo</p>
          </div>
          <div className="p-2 text-center">
            <p className="text-[10px] font-bold text-emerald-500 leading-tight">PortfolioIQ</p>
            <p className="text-[10px] font-semibold text-gray-700 dark:text-slate-300 leading-tight">$29.99/mo</p>
          </div>
          <div className="p-2 text-center dark:bg-indigo-500/[0.03] bg-indigo-50/40">
            <p className="text-[10px] font-bold text-indigo-500 leading-tight">Bundle</p>
            <p className="text-[10px] font-semibold text-gray-700 dark:text-slate-300 leading-tight">$34.99/mo</p>
          </div>
        </div>

        {FEATURES.map(({ label, ws, piq, bundle }, i) => (
          <div
            key={label}
            className={`grid grid-cols-4 dark:border-b dark:border-white/5 border-b border-gray-100 last:border-b-0 ${
              i % 2 === 0 ? "" : "dark:bg-white/[0.015] bg-gray-50/40"
            }`}
          >
            <div className="px-2.5 py-2.5 text-[11px] leading-snug dark:text-slate-300 text-gray-700">{label}</div>
            <div className="px-2 py-2.5 dark:border-l dark:border-white/8 border-l border-blue-200 flex items-center justify-center">
              {ws ? <Y /> : <N />}
            </div>
            <div className="px-2 py-2.5 dark:border-l dark:border-white/8 border-l border-blue-200 flex items-center justify-center">
              {piq ? <Y /> : <N />}
            </div>
            <div className="px-2 py-2.5 dark:border-l dark:border-white/8 border-l border-blue-200 dark:bg-indigo-500/[0.03] bg-indigo-50/30 flex items-center justify-center">
              {bundle ? <Y /> : <N />}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop/tablet layout */}
      <div className="hidden md:block overflow-x-auto rounded-2xl">
        <div className="min-w-[680px] dark:bg-white/[0.02] bg-white border dark:border-white/[0.08] border-blue-300 rounded-2xl overflow-hidden shadow-sm">
        {/* Header row */}
        <div className="grid grid-cols-4 dark:border-b dark:border-white/8 border-b border-gray-200">
          <div className="p-3 sm:p-5" />
          {[
            { name: "WealthScope",  price: "$9.99/mo",  highlight: false, color: "text-blue-400" },
            { name: "PortfolioIQ",  price: "$29.99/mo", highlight: false, color: "text-emerald-400" },
            { name: "Bundle",       price: "$34.99/mo", highlight: true,  color: "text-indigo-400" },
          ].map(({ name, price, highlight, color }) => (
            <div
              key={name}
              className={`p-3 sm:p-5 text-center dark:border-l dark:border-white/8 border-l border-blue-200 ${highlight ? "dark:bg-indigo-500/5 bg-indigo-50/60" : ""}`}
            >
              <p className={`text-sm font-bold ${color} mb-1`}>{name}</p>
              <p className="dark:text-white text-gray-900 font-extrabold text-sm sm:text-base">{price}</p>
            </div>
          ))}
        </div>

        {/* Feature rows */}
        {FEATURES.map(({ label, ws, piq, bundle }, i) => (
          <div
            key={label}
            className={`grid grid-cols-4 dark:border-b dark:border-white/5 border-b border-gray-100 last:border-b-0 ${
              i % 2 === 0 ? "" : "dark:bg-white/[0.015] bg-gray-50/40"
            }`}
          >
            <div className="px-3 sm:px-5 py-3 text-xs sm:text-sm dark:text-slate-400 text-gray-600">{label}</div>
            <div className="px-3 sm:px-5 py-3 dark:border-l dark:border-white/8 border-l border-blue-200 flex items-center justify-center">
              {ws ? <Y /> : <N />}
            </div>
            <div className="px-3 sm:px-5 py-3 dark:border-l dark:border-white/8 border-l border-blue-200 flex items-center justify-center">
              {piq ? <Y /> : <N />}
            </div>
            <div className="px-3 sm:px-5 py-3 dark:border-l dark:border-white/8 border-l border-blue-200 dark:bg-indigo-500/[0.03] bg-indigo-50/30 flex items-center justify-center">
              {bundle ? <Y /> : <N />}
            </div>
          </div>
        ))}
      </div>
      </div>
    </section>
  );
};
